/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import {
  getHotelGroupSchema,
  getHotelGroupASchema,
  getHotelIdSchema,
} from 'modules/dashboard/components/tab-container/inner-filter/functions';

/**
 * Gets hotel codes from a group, excluding invalid hotels when set over time is enabled
 * @param {Object} group - Selected hotel group
 * @param {Boolean} setOverTime - Whether to exclude hotels unavailable over the selected period
 * @param {Array} invalidHotels - Hotels unavailable over the selected period
 * @returns {Array} Hotel codes included in the group filter
 */
const getGroupHotelIds = (group, setOverTime, invalidHotels) => {
  const invalidIds = invalidHotels.map((hotel) => hotel.hotelCode);
  if (!group?.hotels) return [];
  return group.hotels
    .filter((hotel) => !setOverTime || !invalidIds.includes(hotel?.hotelCode))
    .map((hotel) => hotel?.hotelCode);
};

/**
 * Builds hotel ID and comparison group filters for summary and pace visuals
 * Keeps secondary hotel IDs first in the combined visual filter payload.
 * @param {Object} options - Selected hotels, group flags and visual filter configuration
 * @param {Object} options.hotelId - Primary hotel or hotel group
 * @param {Object} options.hotelFocusOn - Comparison hotel or hotel group
 * @param {Boolean} options.enableHotelGroups - Whether the primary selection is a group
 * @param {Boolean} options.enableSecondaryHotelGroups - Whether the comparison selection is a group
 * @param {Boolean} options.isPrimaryHotelSetOverTime - Excludes invalid primary group hotels
 * @param {Array} options.invalidPrimaryHotels - Primary hotels unavailable over the selected period
 * @param {Boolean} options.isSecondaryHotelSetOverTime - Excludes invalid comparison group hotels
 * @param {Array} options.invalidSecondaryHotels - Comparison hotels unavailable over the selected period
 * @param {Array} options.visualFilters - Registered visual filter configuration
 * @returns {Array} Combined hotel ID, secondary group and primary group filter schemas
 */
const buildSummaryHotelFilters = ({
  hotelId,
  hotelFocusOn,
  enableHotelGroups,
  enableSecondaryHotelGroups,
  isPrimaryHotelSetOverTime,
  invalidPrimaryHotels,
  isSecondaryHotelSetOverTime,
  invalidSecondaryHotels,
  visualFilters,
}) => {
  const primaryIds = enableHotelGroups
    ? getGroupHotelIds(hotelId, isPrimaryHotelSetOverTime, invalidPrimaryHotels)
    : [hotelId?.hotelCode];
  const secondaryIds = enableSecondaryHotelGroups
    ? getGroupHotelIds(hotelFocusOn, isSecondaryHotelSetOverTime, invalidSecondaryHotels)
    : [hotelFocusOn?.hotelCode];
  const hotelIds = [...new Set([...secondaryIds, ...primaryIds])];
  return [
    getHotelIdSchema(hotelIds, visualFilters),
    getHotelGroupASchema(secondaryIds),
    getHotelGroupSchema(primaryIds),
  ];
};

export default buildSummaryHotelFilters;
